import type { ReactNode } from 'react';
import clsx from 'clsx';
import { useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { ErrorState, LoadingState } from '../../../components/ui/States';
import { SegmentedControl } from '../../../components/ui/Tabs';
import { useAccess } from '../../../hooks/useAccess';
import { useOutboundActivity } from '../../../hooks/useOutbound';
import { useOutreachOverview } from '../../../hooks/useOutreach';
import { useSdrActivity } from '../../../hooks/useProjectManagement';
import type { SdrActivity } from '../../../api/projectManagement';
import type { OutboundGroup } from '../../../api/outbound';
import type { OutreachOverview, OutreachWeeks } from '../../../api/outreach';
import { SdrDialsCard, SdrDialsTile } from '../../ProjectManagement/SDR/ActivityTab';
import { SectionToolbar } from '../SectionToolbar';
import { CampaignsTable } from '../outreach/CampaignsTable';
import { DetailView } from '../outreach/DetailView';
import { SignalsPanel } from '../outreach/OverviewTable';
import { UnknownCompaniesCard, UnknownCompaniesModal } from '../outreach/UnknownCompanies';
import {
  STALE_SYNC_HOURS,
  formatDateTime,
  formatRelative,
  hoursSince,
  sumFunnels,
  syncFailureText,
} from '../outreach/format';
import { formatDayMonth } from '../../../utils/dateFormat';
import {
  ACTIVITY_INFO,
  RepWeekTable,
  TouchesChart,
  availableFullWeeks,
  coveredWeeksLabel,
  noActivityYet,
  peopleReachedTile,
  periodRange,
  periodTiles,
  replyBridgeText,
  useActivityView,
  type ActivityView,
} from './activity';

type Range = '4' | '12' | 'all';
const DEFAULT_RANGE: Range = '12';

/**
 * The period options. A range that covers every week of lemlist data so far
 * shows the same weeks as "All", so it is left out and "All" says where data
 * starts: with 4 full weeks of data, "4 weeks" and "Since 7 Sep" are the same
 * period and only one is offered.
 */
export function rangeOptions(available: number | null, earliestActivityAt: string | null): { id: Range; label: string }[] {
  if (available === null) {
    return [
      { id: '4', label: '4 weeks' },
      { id: '12', label: '12 weeks' },
      { id: 'all', label: 'All' },
    ];
  }
  const since = earliestActivityAt ? `Since ${formatDayMonth(earliestActivityAt)}` : 'All';
  return [
    ...(available > 4 ? [{ id: '4' as const, label: '4 weeks' }] : []),
    ...(available > 12 ? [{ id: '12' as const, label: '12 weeks' }] : []),
    { id: 'all', label: available > 4 ? `${since} (${available} wks)` : since },
  ];
}

function parseRange(raw: string | null): Range {
  return raw === '4' || raw === 'all' ? raw : DEFAULT_RANGE;
}

type GroupFilter = 'all' | OutboundGroup;

const GROUP_OPTIONS: { id: GroupFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'sdr', label: 'SDRs (Masa, Jovana)' },
  { id: 'ae', label: 'AEs (Nadia, Patrick)' },
];

/** `?group=sdr|ae`; anything else is everyone (Walter is only in All). */
function parseGroup(raw: string | null): OutboundGroup | null {
  return raw === 'sdr' || raw === 'ae' ? raw : null;
}

/** The outbound activity endpoint caps at 52 weeks; lemlist data starts in Sep 2026 anyway. */
const ALL_WEEKS = 52;

const OUTBOUND_INFO = (
  <div className="space-y-2">
    <p>{ACTIVITY_INFO}</p>
    <p>
      Campaigns and signals: for every signal we approach, how many people we put in front of it, how much outreach
      went out and what came back. Outreach numbers come from lemlist, the signal side from the GTM plays queue.
      lemlist activity syncs hourly, the full sync runs daily at ~05:00 UTC.
    </p>
  </div>
);

/**
 * Sales → Outbound: lemlist effort per rep, the SDR dialer, signal plays and
 * campaigns on one page, with one period control. `?signal=` / `?campaign=`
 * open a detail view; `?queue=unknown` opens the people-without-a-company queue.
 */
export function OutboundPage() {
  const { canAccess } = useAccess();
  const [params, setParams] = useSearchParams();
  const range = parseRange(params.get('weeks'));
  const group = parseGroup(params.get('group'));
  const weeks: OutreachWeeks = range === 'all' ? 'all' : Number(range);
  const signal = params.get('signal');
  const campaign = params.get('campaign');
  const queueOpen = params.get('queue') === 'unknown';

  const can = {
    activity: canAccess('sales.outbound'),
    outreach: canAccess('sales.outreach'),
    sdr: canAccess('sales.sdr'),
  };

  const update = (changes: Record<string, string | null>) => {
    const search = new URLSearchParams(params);
    for (const [key, value] of Object.entries(changes)) {
      if (value === null) search.delete(key);
      else search.set(key, value);
    }
    setParams(search);
  };

  /**
   * The period control; a range the data can't fill yet shows as the "All" it
   * equals. With a single possible period there is nothing to choose, so the
   * period is stated as text instead of a one-chip control.
   */
  const rangeControlFor = (earliestActivityAt: string | null) => {
    const available = availableFullWeeks(earliestActivityAt);
    const options = rangeOptions(available, earliestActivityAt);
    const value = options.some((o) => o.id === range) ? range : 'all';
    if (options.length === 1) {
      return (
        <span className="text-body-sm text-text-secondary">
          {options[0]!.label}
        </span>
      );
    }
    return (
      <SegmentedControl
        options={options}
        value={value}
        onChange={(next) => update({ weeks: next === DEFAULT_RANGE ? null : next })}
        ariaLabel="Period"
      />
    );
  };
  const rangeControl = rangeControlFor(null);

  /** Group filter next to the period control; it narrows the lemlist + Apollo activity panels only. */
  const withGroupControl = (period: ReactNode) =>
    can.activity ? (
      <div className="flex flex-wrap items-center gap-2">
        <SegmentedControl
          options={GROUP_OPTIONS}
          value={group ?? 'all'}
          onChange={(next) => update({ group: next === 'all' ? null : next })}
          ariaLabel="Group"
        />
        {period}
      </div>
    ) : (
      period
    );

  const activityWeeks = weeks === 'all' ? ALL_WEEKS : weeks;

  if (can.outreach && (signal || campaign)) {
    // The detail uses the overview's period chips, so it offers the same periods.
    const detail = (detailRangeControl: ReactNode, detailPeriodLabel: string | undefined) => (
      <div className="space-y-4">
        <SectionToolbar
          leading={
            <button
              type="button"
              onClick={() => update({ signal: null, campaign: null })}
              className="inline-flex items-center gap-1 text-body-sm text-text-secondary hover:text-text-primary"
            >
              <ArrowLeft className="h-4 w-4" /> Outbound
            </button>
          }
          actions={detailRangeControl}
        />
        <DetailView kind={signal ? 'signal' : 'campaign'} id={(signal ?? campaign)!} weeks={weeks} periodLabel={detailPeriodLabel} />
      </div>
    );
    return can.activity ? (
      <WithActivity weeks={activityWeeks} group={group}>
        {(view) =>
          detail(
            rangeControlFor(view?.data.earliestActivityAt ?? null),
            view ? coveredWeeksLabel(view, weeks) : undefined,
          )
        }
      </WithActivity>
    ) : (
      detail(rangeControl, undefined)
    );
  }

  const weeksLabel = weeks === 'all' ? 'Every full week since lemlist data starts' : `Last ${weeks} full weeks`;

  return (
    <>
      {can.activity ? (
        <WithActivity weeks={activityWeeks} group={group}>
          {(view, state) => (
            <OutboundBody
              can={can}
              weeks={weeks}
              weeksLabel={weeksLabel}
              rangeControl={withGroupControl(rangeControlFor(view?.data.earliestActivityAt ?? null))}
              activity={view}
              activityState={state}
              onOpenQueue={() => update({ queue: 'unknown' })}
              onOpenSignal={(id) => update({ signal: id, campaign: null })}
              onOpenCampaign={(id) => update({ campaign: id, signal: null })}
            />
          )}
        </WithActivity>
      ) : (
        <OutboundBody
          can={can}
          weeks={weeks}
          weeksLabel={weeksLabel}
          rangeControl={rangeControl}
          activity={null}
          activityState={null}
          onOpenQueue={() => update({ queue: 'unknown' })}
          onOpenSignal={(id) => update({ signal: id, campaign: null })}
          onOpenCampaign={(id) => update({ campaign: id, signal: null })}
        />
      )}
      {can.outreach && queueOpen && <UnknownCompaniesModal isOpen onClose={() => update({ queue: null })} />}
    </>
  );
}

type ActivityState = ReactNode | null;

/** Columns that divide the tile count evenly, so no row ends half empty. */
function tileGridClass(count: number): string {
  if (count >= 6) return 'lg:grid-cols-3 2xl:grid-cols-6';
  if (count === 5) return 'lg:grid-cols-5';
  if (count === 4) return 'lg:grid-cols-4';
  return 'lg:grid-cols-3';
}

/** Fetches the lemlist activity report only for people who may see it. */
function WithActivity({
  weeks,
  group,
  children,
}: {
  weeks: number;
  group: OutboundGroup | null;
  children: (view: ActivityView | null, state: ActivityState) => ReactNode;
}) {
  const query = useOutboundActivity(weeks, group);
  const view = useActivityView(query.data);
  const state = query.error ? (
    <ErrorState title="Couldn’t load lemlist activity" error={query.error} onRetry={() => query.refetch()} />
  ) : !query.data ? (
    <LoadingState rows={4} />
  ) : !query.data.earliestActivityAt ? (
    noActivityYet()
  ) : null;
  return <>{children(state ? null : view, state)}</>;
}

/** Same as WithActivity, for the SDR dialer. */
function WithSdr({ children }: { children: (query: ReturnType<typeof useSdrActivity>) => ReactNode }) {
  const query = useSdrActivity();
  return <>{children(query)}</>;
}

function WithOutreach({
  weeks,
  children,
}: {
  weeks: OutreachWeeks;
  children: (query: ReturnType<typeof useOutreachOverview>) => ReactNode;
}) {
  const query = useOutreachOverview(weeks);
  return <>{children(query)}</>;
}

/** "lemlist synced 3 h ago" — warning-coloured once it is older than a day. */
function LemlistSyncMeta({ data }: { data: OutreachOverview | undefined }) {
  if (!data) return <>lemlist</>;
  const at = data.lemlistLastSyncAt;
  if (!at) return <span className="text-warning-dark">lemlist has never synced: add the API key in Settings → Integrations</span>;
  const stale = hoursSince(at) > STALE_SYNC_HOURS;
  return (
    <span className={stale ? 'font-medium text-warning-dark' : undefined} title={formatDateTime(at)}>
      From lemlist · synced {formatRelative(at)}
      {stale && ': over a day old, numbers are behind'}
    </span>
  );
}

/**
 * The SDR dials chart follows the period: it shows the same weeks as the
 * touches chart (the selected full weeks plus the current one). The Apollo
 * endpoint returns more weeks than lemlist has data for, so "All" means all
 * lemlist weeks, not all Apollo weeks. Without the lemlist report (no access),
 * "4 weeks" keeps the last 4 full weeks plus the current one.
 */
export function sdrForPeriod(data: SdrActivity, weeks: OutreachWeeks, firstWeek: string | null): SdrActivity {
  if (firstWeek) return { ...data, weeks: data.weeks.filter((w) => w.weekStart.slice(0, 10) >= firstWeek) };
  if (weeks === 'all' || weeks + 1 >= data.weeks.length) return data;
  return { ...data, weeks: data.weeks.slice(-(weeks + 1)) };
}

/**
 * The campaign footer, reconciled with the tiles: both count lemlist touches in
 * the same full weeks, so the footer says how much of the tiles' total the
 * listed signal and campaign rows hold, and where the rest went.
 */
function campaignFooter(data: OutreachOverview, periodLabel: string, activity: ActivityView | null): string {
  const listed = sumFunnels([...data.signals.map((s) => s.funnel), ...data.campaigns.map((c) => c.funnel)]).touches;
  const n = (v: number) => v.toLocaleString('en-GB');
  if (!activity) return `${periodLabel} · ${n(listed)} touches in the signal and campaign rows`;
  // The rows are every lemlist campaign, whoever sent; a group view's tiles cover fewer people, so they can't be reconciled.
  if (activity.data.group) return `${periodLabel} · ${n(listed)} lemlist touches in the signal and campaign rows, from everyone`;
  const all = activity.period.touches - activity.period.apolloCalls;
  const rest = all - listed;
  const touches =
    rest > 0
      ? `${periodLabel} · ${n(listed)} of the ${n(all)} lemlist touches above went to the signal and campaign rows; the other ${n(rest)} went to finished or archived campaigns`
      : `${periodLabel} · all ${n(listed)} lemlist touches above are in the signal and campaign rows`;
  const replies = replyBridgeText(data.replyBridge, activity.period.replies);
  return replies ? `${touches}. Replies: ${replies}.` : touches;
}

function OutboundBody({
  can,
  weeks,
  weeksLabel,
  rangeControl,
  activity,
  activityState,
  onOpenQueue,
  onOpenSignal,
  onOpenCampaign,
}: {
  can: { activity: boolean; outreach: boolean; sdr: boolean };
  weeks: OutreachWeeks;
  weeksLabel: string;
  rangeControl: ReactNode;
  activity: ActivityView | null;
  activityState: ActivityState;
  onOpenQueue: () => void;
  onOpenSignal: (playId: string) => void;
  onOpenCampaign: (campaignId: string) => void;
}) {
  const render = (
    outreach: ReturnType<typeof useOutreachOverview> | null,
    sdr: ReturnType<typeof useSdrActivity> | null,
  ) => {
    // What the activity panels actually cover, which can be fewer weeks than selected.
    const activityPeriod = activity ? coveredWeeksLabel(activity, weeks) : weeksLabel;
    const tiles = activity ? periodTiles(activity, outreach?.data?.replyBridge) : [];
    // People reached and SDR dials can't follow the period (distinct people and dials don't add up across
    // weeks): they join the period tiles in one band, and each says "Last full week" in its own sub-line.
    // SDR dials are the SDRs' own number, so the AE view leaves them out.
    const showDials = activity?.data.group !== 'ae';
    const lastWeekTiles = [
      ...(activity && activity.data.group !== 'sdr' ? [peopleReachedTile(activity)] : []),
      ...(sdr && showDials ? [<SdrDialsTile key="dials" query={sdr} />] : []),
    ];
    const allTiles = [...tiles, ...lastWeekTiles];
    const sdrData = sdr?.data ? sdrForPeriod(sdr.data, weeks, activity?.data.weeks[0] ?? null) : null;
    const failure = outreach?.data?.lemlistSyncFailure;
    return (
      <div className="space-y-5">
        <SectionToolbar
          meta={can.outreach ? <LemlistSyncMeta data={outreach?.data} /> : 'From lemlist'}
          info={OUTBOUND_INFO}
          actions={rangeControl}
        />

        {failure && (
          <ErrorState
            compact
            title={`The latest ${failure.run === 'daily' ? 'daily lemlist sync' : 'hourly lemlist activity sync'} failed`}
            error={`${formatRelative(failure.at)}: numbers since then may be missing.`}
            details={syncFailureText(failure)}
          />
        )}

        {activityState}

        {allTiles.length > 0 && (
          <div className="space-y-2">
            {/* One period story: the period is said once here; the two tiles that can't follow it say so themselves. */}
            <p className="text-body-xs text-text-tertiary">
              {activity ? `${activityPeriod} · ${periodRange(activity)}` : 'Last full week'}
              {lastWeekTiles.length > 0 && tiles.length > 0 && ' · People reached and SDR dials are the last full week only'}
            </p>
            <div className={clsx('grid grid-cols-2 gap-4', tileGridClass(allTiles.length))}>{allTiles}</div>
          </div>
        )}

        {can.outreach && <UnknownCompaniesCard onOpen={onOpenQueue} />}

        {activity && <RepWeekTable view={activity} />}
        {activity && <TouchesChart view={activity} weeksLabel={activityPeriod} />}

        {outreach &&
          (outreach.error ? (
            <ErrorState title="Couldn’t load campaigns" error={outreach.error} onRetry={() => outreach.refetch()} />
          ) : !outreach.data ? (
            <LoadingState rows={4} />
          ) : (
            <>
              <SignalsPanel data={outreach.data} onOpenSignal={onOpenSignal} />
              <CampaignsTable
                rows={outreach.data.campaigns}
                onOpen={onOpenCampaign}
                title="Campaigns"
                warnings={activity?.sequenceWarnings}
                footer={campaignFooter(outreach.data, activityPeriod, activity)}
              />
            </>
          ))}

        {sdrData && showDials && <SdrDialsCard data={sdrData} />}
        {sdr?.error && <ErrorState title="Couldn’t load SDR dials" error={sdr.error} onRetry={() => sdr.refetch()} />}
      </div>
    );
  };

  const withSdr = (outreach: ReturnType<typeof useOutreachOverview> | null) =>
    can.sdr ? <WithSdr>{(sdr) => render(outreach, sdr)}</WithSdr> : render(outreach, null);

  return can.outreach ? <WithOutreach weeks={weeks}>{(outreach) => withSdr(outreach)}</WithOutreach> : withSdr(null);
}
